import fs from "node:fs/promises";
import os from "node:os";
import path from "node:path";

export async function fileExists(
  path: string | null | undefined,
): Promise<boolean> {
  if (path == null) {
    return false;
  }
  try {
    await fs.access(path, fs.constants.F_OK);
    return true;
  } catch {
    return false;
  }
}

/**
 * Calculates the maximum number of worker threads to use.
 * Always returns a positive integer.
 *
 * The calculation considers:
 * - The target number of desired workers
 * - The system's available parallelism (via `os.availableParallelism()`)
 *   - One thread is subtracted to reserve capacity for the main process
 * - A lower bound of 1
 *
 * @param target - The desired number of workers to use.
 *
 * @returns The computed number of workers to use.
 *
 * @example
 * ```ts
 * const workers = getMaxWorkers(8);
 * console.log(`Using ${workers} worker threads`);
 * ```
 */
export function getMaxWorkers(target: number): number {
  const maxAvailable = os.availableParallelism() - 1;
  return Math.max(1, Math.min(target, maxAvailable));
}

export function getDupes<V>(values: Iterable<V>): [number[], number[]];
export function getDupes<K, V>(
  values: Iterable<V>,
  keyFn: (value: V) => K,
): [number[], number[]];
export function getDupes<V, K = V>(
  values: Iterable<V>,
  keyFn?: (value: V) => K,
): [number[], number[]] {
  const set = new Set<K>();
  const dupes: number[] = [];
  const valid: number[] = [];

  keyFn ??= (value: V) => value as unknown as K;

  let index = 0;
  for (const value of values) {
    const key = keyFn!(value);
    if (set.has(key)) {
      dupes.push(index);
    } else {
      set.add(key);
      valid.push(index);
    }
    ++index;
  }

  return [valid, dupes];
}

export async function readJSONFile<T>(filePath: string): Promise<T> {
  const buffer = await fs.readFile(path.resolve(filePath));
  return JSON.parse(buffer.toString());
}
